import { RoutePath } from '@/src/RoutePath';
import FileDragAndDrop from '@/src/components/common/FileDragAndDrop';
import { KeyValue } from '@/src/components/common/KeyValue';
import { useFlashbars } from '@/src/store/flashbar.context';
import { downloadAs } from '@/src/utils/download.utils';
import CodeView from '@amzn/awsui-code-view/code-view';
import yamlHighlight from '@amzn/awsui-code-view/highlight/yaml';
import { Box, Button, Container, Header, SpaceBetween, TextContent } from '@amzn/awsui-components-react';
import * as awsui from '@amzn/awsui-design-tokens/polaris.js';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChallenges } from '../../../../store/challenge.context';
import { ChallengePropAction, useCreateChallenge } from '../../../../store/create-challenge.context';
import { Challenge, ChallengeReviewableSection } from '../../../../types/Challenge';
import {
  hasAwsJamResourceWithoutRegion,
  hasValidCfnTemplateResource,
  parseCfnToJson,
} from '../../../../utils/cfn.utils';
import { getEnvVar } from '../../../../utils/env-var.utils';
import { i18nKeys } from '../../../../utils/i18n.utils';
import { preProdLogger } from '../../../../utils/log.utils';
import { ChallengeReviewPopover } from '../../challengesCommon/ChallengeReviewPopover';
import JamCodeEditor from '../../challengesCommon/JamCodeEditor';

const riverLcsrLink =
  'https://river.amazon.com/PDX13/workflows?buildingType=AWSMarketing&q0=59f88da2-2a62-45cd-8de1-faa1b8874470&id=59f88da2-2a62-45cd-8de1-faa1b8874470';

interface ChallengeCFNTemplateDetailProps {
  challenge: Challenge;
}

const ChallengeCFNTemplateDetail: React.FC<ChallengeCFNTemplateDetailProps> = ({ challenge }) => {
  const { t } = useTranslation();
  const { addSuccessFlashbar, addWarningFlashbar } = useFlashbars();
  const { getChallengeResources, getChallengeConfiguration, validateCfnTemplate } = useChallenges();
  const { editMode, editedChallenge, handleUpdateChallengeProp } = useCreateChallenge();
  const [templateScanResults, setTemplateScanResults] = useState('');
  const [disableValidation, setDisableValidation] = useState(true);
  const [fetchingTemplateValidation, fetchingTemplateValidationSet] = useState(false);

  const challengeResourceAccountName = getEnvVar('REACT_APP_CHALLENGE_RESOURCES_BUCKET_NAME');

  const originalTemplate = useRef(challenge.props.cfnTemplate);

  useEffect(() => {
    getChallengeConfiguration();
    void getChallengeResources(challenge.challengeId || '');
  }, []);

  useEffect(() => {
    setTemplateScanResults('');
  }, [editMode]);

  useEffect(() => {
    if (editedChallenge?.props.cfnTemplate?.length !== 0) {
      setDisableValidation(false);
    } else {
      setDisableValidation(true);
    }
  }, [editedChallenge?.props.cfnTemplate]);

  const handleFileUpload = (uploadedFile: File) => {
    // Handle file upload
    uploadedFile
      .text()
      .then((file) => {
        handleUpdateChallengeProp(ChallengePropAction.CFN_TEMPLATE, file);
      })
      .catch((err) => preProdLogger(err));
  };

  const handleUpdateCfnTemplate = (template: string) => {
    if (editMode) {
      handleUpdateChallengeProp(ChallengePropAction.CFN_TEMPLATE, template);
    }
  };

  const challengeResourceBucket = getEnvVar('REACT_APP_CHALLENGE_RESOURCES_BUCKET_NAME') || '';
  const cfnTemplateFileName = `aws-jam-${challenge.challengeId}-cfn-template.yaml`;

  const onValidateCFNTemplate = () => {
    fetchingTemplateValidationSet(true);
    validateCfnTemplate(challenge.props.cfnTemplate || '', true)
      .then((results) => {
        if (results) {
          const warnings = /^Warnings count:\s*(\d+)/m.exec(results);
          const failures = /^Failures count:\s*(\d+)/m.exec(results);
          const numberOfWarnings = warnings && warnings?.length > 1 ? parseInt(warnings[1], 10) : 0;
          const numberOfFailures = failures && failures?.length > 1 ? parseInt(failures[1], 10) : 0;
          if (numberOfWarnings + numberOfFailures > 0) {
            addWarningFlashbar(t(i18nKeys.challenges.subSections.cfnTemplate.validateTemplateWarning));
          } else {
            addSuccessFlashbar(t(i18nKeys.success.requestSucceeded.challenges.validateTemplate));
          }
        }
        setTemplateScanResults(results);
      })
      .catch((err) => preProdLogger(err))
      .finally(() => fetchingTemplateValidationSet(false));
  };

  const hasInvalidCfnTemplateResource = () => {
    if (editedChallenge) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const cfnJson = parseCfnToJson(editedChallenge.props.cfnTemplate || '');
      return !hasValidCfnTemplateResource(cfnJson);
    }
  };

  const hasMissingResourceRegion = () => {
    if (editedChallenge) {
      return hasAwsJamResourceWithoutRegion(editedChallenge.props.cfnTemplate || '');
    }
  };

  return (
    <Container
      header={
        <>
          <Box float="left">
            <Header variant="h2" actions={<ChallengeReviewPopover section={ChallengeReviewableSection.CFN_TEMPLATE} />}>
              {t(i18nKeys.challenges.challengeDetails.headings.cloudformationTemplate)}
            </Header>
          </Box>
          <Box float="right">
            <SpaceBetween size="s" direction="horizontal" alignItems="center">
              <Button href={riverLcsrLink} target="_blank" external rel='noopener noreferrer' data-testid="lcsr-scanner-link">
                {t(i18nKeys.challenges.subSections.cfnTemplate.openLcsrScanner)}
              </Button>
              <Button
                disabled={disableValidation}
                data-testid="challenge-cnftemplate-validate-cfn-template"
                iconName={!!templateScanResults.length ? 'status-positive' : 'status-info'}
                loading={fetchingTemplateValidation}
                onClick={onValidateCFNTemplate}
              >
                {t(i18nKeys.challenges.subSections.cfnTemplate.validateTemplate)}
              </Button>
            </SpaceBetween>
          </Box>
        </>
      }
    >
      <Box margin={{ bottom: 'm' }}>
        <TextContent>
          {`${t(i18nKeys.challenges.subSections.cfnTemplate.infoFlashbar)} ${t(i18nKeys.general.avoidPIIWarning)}`}
        </TextContent>
        <br />
        <TextContent>{t(i18nKeys.challenges.subSections.cfnTemplate.openLcsrScannerDescription)}</TextContent>
      </Box>
      <SpaceBetween size="s">
        {editMode && (
          <div
            style={{
              backgroundColor: awsui.colorBackgroundCellShaded,
              padding: '10px',
              borderRadius: '8px',
            }}
          >
            <KeyValue
              className="primary-text"
              label={
                <TextContent>
                  <p>
                    <strong>{t(i18nKeys.challenges.subSections.cfnTemplate.toolsAndResources)}</strong>
                  </p>
                </TextContent>
              }
            >
              <SpaceBetween size="xs" direction="vertical">
                <Button
                  variant="link"
                  data-testid="challenge-cnftemplate-show-snippet"
                  href={RoutePath.CHALLENGE_SNIPPETS}
                  iconAlign="left"
                  iconName="external"
                  target="_blank"
                >
                  {t(i18nKeys.challenges.challengeDetails.buttons.showCfnSnippets)}
                </Button>
                <Button
                  variant="link"
                  data-testid="challenge-cnftemplate-provide-ssh-access"
                  href={RoutePath.SSH_ACCESS}
                  iconAlign="left"
                  iconName="external"
                  target="_blank"
                >
                  {t(i18nKeys.challenges.challengeDetails.buttons.provideSshAccess)}
                </Button>
                <Button
                  variant="link"
                  data-testid="challenge-cnftemplate-using-challenge-resource"
                  href={`${RoutePath.CHALLENGE_RESOURCES}${challenge.id}`}
                  iconAlign="left"
                  iconName="external"
                  target="_blank"
                >
                  {t(i18nKeys.challenges.challengeDetails.buttons.usingChallengeResources)}
                </Button>
              </SpaceBetween>
            </KeyValue>
          </div>
        )}
        {editMode && (
          <Box padding={'l'} textAlign="center">
            <FileDragAndDrop
              validMimes={['application/x-yaml', 'text/yaml']}
              onUpdate={(files: File[]) => handleFileUpload(files[0])}
              constraintText={
                <>
                  <strong>{t(i18nKeys.challenges.subSections.cfnTemplate.format.title)} </strong>
                  {t(i18nKeys.challenges.subSections.cfnTemplate.format.part1)}{' '}
                  <a href="https://github.com/awslabs/aws-cfn-template-flip">aws-cfn-template-flip</a>
                  {t(i18nKeys.challenges.subSections.cfnTemplate.format.part2)}{' '}
                  <a href="https://us-west-2.console.aws.amazon.com/cloudformation/designer/home?region=us-west-2">
                    CloudFormation Template Designer
                  </a>
                  {t(i18nKeys.challenges.subSections.cfnTemplate.format.part3)}
                </>
              }
            />
          </Box>
        )}
        {challenge.hasCfnTemplate && !editMode && (
          <Button
            variant="link"
            data-testid="challenge-cfn-template-download"
            onClick={() => downloadAs(challenge.props.cfnTemplate || '', cfnTemplateFileName, 'text/yaml')}
            iconAlign="left"
            iconName="file"
          >
            {cfnTemplateFileName}
          </Button>
        )}
        {editMode && (
          <JamCodeEditor
            language="yaml"
            value={editedChallenge?.props.cfnTemplate || ''}
            setValue={(e) => {
              handleUpdateCfnTemplate(e);
            }}
            data-testid='cfn-markdown-editor'
          />
        )}
        {!editMode && <CodeView highlight={yamlHighlight} content={challenge.props.cfnTemplate || ''} />}
        {templateScanResults.length > 0 && (
          <Container
            header={<Header>{t(i18nKeys.challenges.challengeDetails.headings.templateSecurityFindings)}</Header>}
          >
            <CodeView highlight={yamlHighlight} content={templateScanResults} />
          </Container>
        )}

        {!editMode && challenge.hasCfnTemplate && (
          <div>
            {(hasMissingResourceRegion() || hasInvalidCfnTemplateResource()) && (
              <div style={{ fontSize: '1rem' }}>
                <div className="aws-panel aws-panel-info mb-3">
                  <span className="fa fa-info-circle" />
                  {hasMissingResourceRegion() && (
                    <Box>
                      {t(i18nKeys.challenges.challengeDetails.text.missingResourcePartOne)}{' '}
                      <strong>{challengeResourceAccountName}</strong>{' '}
                      {t(i18nKeys.challenges.challengeDetails.text.missingResourcePartTwo)}
                    </Box>
                  )}
                  {hasInvalidCfnTemplateResource() && (
                    <span style={{ display: hasMissingResourceRegion() ? 'block' : 'inline' }}>
                      {hasMissingResourceRegion() && <br />}
                      {t(i18nKeys.challenges.challengeDetails.text.nestedCfnTemplate)}
                      <strong>{`${challengeResourceBucket} ${t(
                        i18nKeys.challenges.challengeDetails.text.bucket
                      )}`}</strong>
                      .
                    </span>
                  )}

                  <div>
                    <br />
                    {t(i18nKeys.challenges.challengeDetails.text.mayDelayApproval)}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default ChallengeCFNTemplateDetail;
